import React, { useState } from 'react';
import {
  TextField,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Paper,
  Alert,
  useTheme,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ModalMAIQ from "app/components/ModalMAIQ";
import { Resource, ResourceType } from '../../types';
import { useResource } from 'app/context/ResourceContext';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';

interface AddResourceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddResourceModal: React.FC<AddResourceModalProps> = ({ open, onClose, onSuccess }) => {
  const { addResource } = useResource();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const theme = useTheme();
  const [formData, setFormData] = useState<Partial<Resource>>({
    type: 'PAGE',
    name: '',
    path: '',
    component: '',
  });

  const resourceType = (formData.type ?? 'PAGE') as ResourceType;
  const isPageType = resourceType === 'PAGE';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    
    // Special handling for path field to add '/' prefix if needed
    if (name === 'path' && typeof value === 'string') {
      const pathValue = value.startsWith('/') ? value : `/${value}`;
      setFormData(prev => ({
        ...prev,
        [name]: pathValue,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name as string]: value,
      }));
    }
  };

  const handleTypeChange = (e: SelectChangeEvent<ResourceType>) => {
    const type = e.target.value as ResourceType;
    setFormData(prev => ({ ...prev, type }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPageType && !formData.path?.trim()) {
      setError('Path is required for PAGE type');
      return;
    }
    setIsSubmitting(true);
    setError('');

    try {
      const payload: Partial<Resource> = {
        type: resourceType,
        name: formData.name,
        component: formData.component,
      };
      if (resourceType === 'PAGE' && formData.path != null) {
        payload.path = formData.path.startsWith('/') ? formData.path : `/${formData.path}`;
      }
      // COMPONENT: do not send path in API payload
      await addResource(payload);
      onSuccess();
      onClose();
      setFormData({
        type: 'PAGE',
        name: '',
        path: '',
        component: '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalMAIQ 
      open={open} 
      onClose={onClose} 
      hideDefaultCloseButton={true}
    >
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddCircleOutlineIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                Add New Resource
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Type *
            </Typography>
            <FormControl fullWidth size="small" required disabled={isSubmitting}>
              <Select
                name="type"
                value={resourceType}
                onChange={handleTypeChange}
                displayEmpty
                renderValue={(value) => (
                  <Chip
                    size="small"
                    label={value === 'PAGE' ? 'Page' : 'Component'}
                    sx={{
                      backgroundColor: value === 'PAGE' ? theme.palette.primary.main : theme.palette.secondary.main,
                      color: 'white',
                      '& .MuiChip-label': { color: 'white' },
                    }}
                  />
                )}
                inputProps={{ sx: { py: 1.5 } }}
              >
                <MenuItem value="PAGE">Page</MenuItem>
                <MenuItem value="COMPONENT">Component</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Resource Name *
            </Typography>
            <TextField
              fullWidth
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter resource name"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          {isPageType && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Path *
              </Typography>
              <TextField
                fullWidth
                name="path"
                value={formData.path ?? ''}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                variant="outlined"
                size="small"
                placeholder="Enter path (/ will be added automatically)"
                inputProps={{
                  sx: { py: 1.5 }
                }}
                helperText="Forward slash (/) will be added automatically if not provided"
              />
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              {isPageType ? 'Page *' : 'Component *'}
            </Typography>
            <TextField
              fullWidth
              name="component"
              value={formData.component}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter component name"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>
        </Box>

        {/* Footer with actions - fixed */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2
          }}
        >
          <ButtonMAIQ
            variant="outlined"
            color="primary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </ButtonMAIQ>
          <Tooltip title="Create a new resource">
            <ButtonMAIQ
              variant="contained"
              type="submit"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            >
              {isSubmitting ? 'Creating...' : 'Create Resource'}
            </ButtonMAIQ>
          </Tooltip>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

export default AddResourceModal; 